import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type FileDownloadResponse,
  formatMoney,
  type InvoiceResponse,
  MAX_UPLOAD_BYTES,
  proformaAllocation,
  type ProformaReplaceRequest,
  type SubjectFilesResponse,
  toCents,
} from '@k-management/shared';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { apiRequest, apiUpload } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { treasuryKeys } from './treasury-ui.js';

/**
 * A pro forma and its invoices (1.59): the invoices of Gestify of the same
 * supplier that may replace it, the likely first; the payments made on it and
 * the invoice each passes to (the oldest invoice first, changeable). Once
 * replaced: its invoices, the difference (the invoices rule), more invoices
 * when the balance comes later, and the undo.
 */
export function ProformaPanel({
  invoice,
  editable,
  onSaved,
}: {
  invoice: InvoiceResponse;
  editable: boolean;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const view = invoice.proforma!;
  const replaced = view.replacedBy.length > 0;
  const [chosen, setChosen] = useState<string[]>(() =>
    replaced
      ? []
      : view.candidates
          .filter((item) => item.likely)
          .slice(0, 1)
          .map((item) => item.id),
  );
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [restoring, setRestoring] = useState(false);
  // Payments still on the pro forma go to the invoices, old and new.
  const targets = [
    ...view.replacedBy,
    ...view.candidates.filter((item) => chosen.includes(item.id)),
  ];
  const proposal = new Map(
    proformaAllocation(invoice.settlements, targets).map((item) => [
      item.settlementId,
      item.invoiceId,
    ]),
  );
  const allocation = invoice.settlements.map((settlement) => ({
    settlementId: settlement.id,
    invoiceId: overrides[settlement.id] ?? proposal.get(settlement.id) ?? '',
  }));
  const after = async (updated: InvoiceResponse) => {
    setChosen([]);
    setOverrides({});
    await onSaved(updated);
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
  };
  const replace = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/replacements`, {
        method: 'POST',
        body: { invoiceIds: chosen, settlements: allocation } satisfies ProformaReplaceRequest,
      }),
    onSuccess: after,
  });
  const restore = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/replacements`, {
        method: 'DELETE',
      }),
    onSuccess: async (updated) => {
      setRestoring(false);
      await after(updated);
    },
  });
  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  const chosenNet = targets.reduce((sum, item) => sum + toCents(item.netDue), 0);
  const difference = chosenNet - toCents(invoice.netDue);

  return (
    <div className="card stack">
      <h2>{replaced ? 'Sostituita dalle fatture' : 'Fattura che la sostituisce'}</h2>
      {replaced && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fattura</th>
                  <th>Data</th>
                  <th className="numeric">Netto</th>
                </tr>
              </thead>
              <tbody>
                {view.replacedBy.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Link to={`/tesoreria/fatture/${item.id}`}>{item.number}</Link>
                    </td>
                    <td>{formatDate(item.date)}</td>
                    <td className="numeric">{formatMoney(item.netDue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {view.difference && (
            <p className="notice small">
              Le fatture {Number(view.difference) > 0 ? 'superano' : 'sono sotto'} la pro-forma di{' '}
              <strong>{formatMoney(view.difference.replace('-', ''))}</strong>: comandano le
              fatture, il residuo è calcolato sui loro importi.
            </p>
          )}
          <p className="muted small">
            La pro-forma esce dai totali; i pagamenti fatti su di essa sono passati alle fatture.
          </p>
          {editable && view.undoable && (
            <div className="actions">
              <button type="button" onClick={() => setRestoring(true)}>
                Annulla la sostituzione
              </button>
            </div>
          )}
        </>
      )}

      {editable && (
        <>
          {replaced && view.candidates.length > 0 && (
            <h3>Arriva un’altra fattura (per esempio il saldo)?</h3>
          )}
          {!replaced && view.candidates.length === 0 && (
            <p className="muted">
              Nessuna fattura di Gestify del fornitore da una settimana prima della pro-forma in
              poi: quando arriva, con «Aggiorna da Gestify», la trovi qui.
            </p>
          )}
          {view.candidates.length > 0 && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fattura</th>
                    <th>Data</th>
                    <th className="numeric">Netto</th>
                  </tr>
                </thead>
                <tbody>
                  {view.candidates.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={chosen.includes(item.id)}
                            onChange={() => toggle(item.id)}
                          />{' '}
                          <Link to={`/tesoreria/fatture/${item.id}`}>{item.number}</Link>
                        </label>
                        {item.likely && <span className="pill info">importo vicino</span>}
                      </td>
                      <td>{formatDate(item.date)}</td>
                      <td className="numeric">{formatMoney(item.netDue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {chosen.length > 0 && invoice.settlements.length > 0 && (
            <>
              <h3>Dove passano i pagamenti della pro-forma</h3>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Pagamento</th>
                      <th className="numeric">Importo</th>
                      <th>Passa alla fattura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.settlements.map((settlement, index) => (
                      <tr key={settlement.id}>
                        <td>{formatDate(settlement.date)}</td>
                        <td className="numeric">{formatMoney(settlement.amount)}</td>
                        <td>
                          <select
                            aria-label={`Fattura del pagamento del ${formatDate(settlement.date)}`}
                            value={allocation[index]!.invoiceId}
                            onChange={(event) =>
                              setOverrides((current) => ({
                                ...current,
                                [settlement.id]: event.target.value,
                              }))
                            }
                          >
                            {targets.map((target) => (
                              <option key={target.id} value={target.id}>
                                {target.number} del {formatDate(target.date)}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {chosen.length > 0 && (
            <div className="row between">
              <p className="muted small">
                {difference === 0
                  ? 'Le fatture tornano con la pro-forma.'
                  : `Le fatture ${difference > 0 ? 'superano' : 'sono sotto'} la pro-forma di ${formatMoney((Math.abs(difference) / 100).toFixed(2))}: comandano le fatture.`}
              </p>
              <button
                type="button"
                className="primary"
                disabled={replace.isPending}
                onClick={() => replace.mutate()}
              >
                {replace.isPending
                  ? 'Attendere…'
                  : replaced
                    ? 'Aggiungi alla sostituzione'
                    : 'Sostituisci la pro-forma'}
              </button>
            </div>
          )}
          {replace.error && <p className="error">{replace.error.message}</p>}
        </>
      )}

      {restoring && (
        <ConfirmDialog
          title="Annullare la sostituzione?"
          confirmLabel="Annulla la sostituzione"
          busy={restore.isPending}
          error={restore.error?.message}
          onConfirm={() => restore.mutate()}
          onCancel={() => setRestoring(false)}
        >
          <p>
            La pro-forma torna da pagare e i suoi pagamenti tornano su di essa. Resta nel registro
            attività.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * The PDF of a document entered by hand (1.59): uploaded in the folder of the
 * supplier on SharePoint, optional. Needs the documents of Anagrafica too.
 */
export function ManualAttachment({
  invoice,
  editable,
  onSaved,
}: {
  invoice: InvoiceResponse;
  editable: boolean;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const canDocuments = useCan('anagrafica.documents');
  const canRegistryWrite = useCan('anagrafica.write');
  const canUpload = editable && canDocuments && canRegistryWrite;
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const base = `/subjects/${invoice.subjectId}/files`;
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_UPLOAD_BYTES) throw new Error('File troppo grande (al massimo 20 MB)');
      const name = file.name;
      const files = await apiUpload<SubjectFilesResponse>(
        `${base}?name=${encodeURIComponent(name)}`,
        file,
      );
      // The file just uploaded: by name, else the newest.
      const item =
        files.items.find((entry) => entry.name === name) ??
        [...files.items].sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt))[0];
      if (!item) throw new Error('Il file non risulta nella cartella del fornitore');
      return apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/attachment`, {
        method: 'PUT',
        body: { attachment: { itemId: item.id, name: item.name } },
      });
    },
    onSuccess: async (updated) => {
      setError('');
      await onSaved(updated);
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const open = useMutation({
    mutationFn: () =>
      apiRequest<FileDownloadResponse>(`${base}/${invoice.attachment!.itemId}/download`),
    onSuccess: (link) => window.open(link.url, '_blank', 'noopener'),
    onError: (failure: Error) => setError(failure.message),
  });
  if (!invoice.subjectId) return null;
  return (
    <div className="card stack">
      <div className="row between">
        <h2>PDF del documento</h2>
        <div className="row">
          {invoice.attachment && canDocuments && (
            <button type="button" disabled={open.isPending} onClick={() => open.mutate()}>
              Apri {invoice.attachment.name}
            </button>
          )}
          {canUpload && (
            <>
              <input
                ref={input}
                type="file"
                accept="application/pdf,image/*"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) upload.mutate(file);
                }}
              />
              <button
                type="button"
                disabled={upload.isPending}
                onClick={() => input.current?.click()}
              >
                {upload.isPending
                  ? 'Caricamento…'
                  : invoice.attachment
                    ? 'Sostituisci il PDF'
                    : 'Allega il PDF'}
              </button>
            </>
          )}
        </div>
      </div>
      <p className="muted small">
        {invoice.attachment
          ? 'Nella cartella del fornitore su SharePoint.'
          : 'Facoltativo: va nella cartella del fornitore su SharePoint.'}
        {editable && !canUpload && ' Per allegarlo servono i documenti dell’Anagrafica.'}
      </p>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
